import React, { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { Layers, TrendingUp, Sparkles, Server, Zap, Compass, ArrowRight } from 'lucide-react';
import { expertisesData } from '../data/mock';

/**
 * ExpertisesSection Component
 * 
 * Section "Nos Expertises" avec grille de 6 domaines.
 * Animations:
 * - Entrée en cascade au scroll
 * - Hover avec effet de mise en avant
 * - Icônes animées
 */

const iconMap = {
  Layers,
  TrendingUp,
  Sparkles,
  Server,
  Zap,
  Compass,
};

const ExpertisesSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: false, margin: '-10%' });
  const [hoveredCard, setHoveredCard] = useState(null);
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };
  
  const cardVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: 'spring',
        damping: 20,
        stiffness: 100,
      },
    },
  };
  
  return (
    <section
      id="expertises"
      ref={sectionRef}
      className="section-container bg-[#0A0A0A]"
    >
      {/* Section Header */}
      <motion.div
        className="text-center mb-20"
        initial={{ opacity: 0, y: 30 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6 }}
      >
        <p className="text-[#2A3B8F] uppercase tracking-widest text-sm mb-4">
          Domaines d'opportunité
        </p>
        <h2 className="heading-lg mb-6">
          Nos expertises
        </h2>
        <p className="body-md text-[#888888] max-w-2xl mx-auto">
          Nous ne proposons pas des services. Nous identifions des domaines 
          où l'opportunité technologique crée l'avantage stratégique.
        </p>
      </motion.div>
      
      {/* Expertise Grid */}
      <motion.div
        className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
        variants={containerVariants}
        initial="hidden"
        animate={isInView ? 'visible' : 'hidden'}
      >
        {expertisesData.map((expertise, index) => {
          const IconComponent = iconMap[expertise.icon];
          const isHovered = hoveredCard === index;
          const isOtherHovered = hoveredCard !== null && hoveredCard !== index;
          
          return (
            <motion.div
              key={expertise.id}
              variants={cardVariants}
              className={`relative p-8 bg-[#1A1A1A] border border-white/5 group interactive transition-opacity duration-300 ${
                isOtherHovered ? 'opacity-40' : 'opacity-100'
              }`}
              onMouseEnter={() => setHoveredCard(index)}
              onMouseLeave={() => setHoveredCard(null)}
              whileHover={{
                y: -8,
                borderColor: 'rgba(42, 59, 143, 0.5)',
                transition: { type: 'spring', stiffness: 300 },
              }}
            >
              {/* Icon */}
              <motion.div
                className="w-14 h-14 flex items-center justify-center mb-6 border border-[#2A3B8F]/30 bg-[#2A3B8F]/10"
                whileHover={{
                  scale: 1.1,
                  backgroundColor: 'rgba(42, 59, 143, 0.2)',
                  rotate: 5,
                }}
              >
                {IconComponent && (
                  <motion.div
                    animate={isHovered ? { scale: 1.2 } : { scale: 1 }}
                    transition={{ type: 'spring', stiffness: 400 }}
                  >
                    <IconComponent className="w-7 h-7 text-[#2A3B8F]" />
                  </motion.div>
                )}
              </motion.div>
              
              {/* Title */}
              <h3 className="text-lg font-semibold text-white mb-3 tracking-wide">
                {expertise.title}
              </h3>
              
              {/* Description */}
              <p className="text-[#888888] text-sm leading-relaxed mb-6">
                {expertise.description}
              </p>
              
              {/* Hover CTA */}
              <motion.div
                className="flex items-center gap-2 text-[#2A3B8F] text-sm"
                initial={{ opacity: 0, x: -10 }}
                animate={isHovered ? { opacity: 1, x: 0 } : { opacity: 0, x: -10 }}
                transition={{ duration: 0.2 }}
              >
                <span className="uppercase tracking-wider">Explorer</span>
                <ArrowRight size={14} />
              </motion.div>
              
              {/* Bottom accent line on hover */}
              <motion.div
                className="absolute bottom-0 left-0 h-0.5 bg-[#2A3B8F]"
                initial={{ width: 0 }}
                animate={{ width: isHovered ? '100%' : 0 }}
                transition={{ duration: 0.3 }}
              />
            </motion.div>
          );
        })}
      </motion.div>
    </section>
  );
};

export default ExpertisesSection;
